// graphql-ws's default backoff: 1s doubling, plus 300ms–3s of jitter. RUM shows 99% of
// socket outages recovering within its first five steps (~53s), so only later steps hit the cap.
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30_000;
const MIN_JITTER_MS = 300;
const MAX_JITTER_MS = 3000;

const canReconnect = () => document.visibilityState === 'visible' && navigator.onLine;

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

const untilReconnectable = () =>
  new Promise<void>(resolve => {
    const check = () => {
      if (!canReconnect()) return;
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('online', check);
      resolve();
    };
    document.addEventListener('visibilitychange', check);
    window.addEventListener('online', check);
  });

/**
 * graphql-ws `retryWait`. Never attempts while the tab is hidden or the browser is offline,
 * and attempts straight away once both clear, rather than finishing a backoff the user waits on.
 */
export async function waitToReconnect(retries: number) {
  if (!canReconnect()) {
    await untilReconnectable();
    return;
  }

  const jitter = MIN_JITTER_MS + Math.random() * (MAX_JITTER_MS - MIN_JITTER_MS);
  await sleep(Math.min(BASE_DELAY_MS * 2 ** retries, MAX_DELAY_MS) + jitter);

  if (!canReconnect()) await untilReconnectable();
}
